import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';
import {
  areUnitIdsEqual,
  buildManufacturerCardsFromUnits,
  getSortedUnitIds,
  getUnitIdFromNodeId,
} from 'utils/AICreatorHelper';

import type { IAICardSelection } from 'components/AICardPanel/interfaces/IAICardSelection';
import type IAgentSessionBomProducedByUnit from 'store/slices/valueChainMap/interfaces/IAgentSessionBomProducedByUnit';
import type IAICreatorBoMUnitsTabItem from '../interfaces/IAICreatorBoMUnitsTabItem';
import type IManufacturerSyncState from '../interfaces/IManufacturerSyncState';
import type IUseAICreatorBoMManufacturersParams from '../interfaces/IUseAICreatorBoMManufacturersParams';
import type { TManufacturerCardsByBomId } from '../interfaces/TManufacturerCardsByBomId';

const useAICreatorBoMManufacturers = ({
  open,
  currentBomId,
  availableManufacturerCards,
  producedByUnits,
  valueChainMapId,
  session,
  activeTab,
  updateAgentSessionBom,
  onAddManufacturer,
  isManufacturerEditingDisabled,
  onBlockedManufacturerEdit,
  onBadRequestError,
}: IUseAICreatorBoMManufacturersParams) => {
  const previousBomIdRef = useRef<number>(0);
  const pendingManufacturerSyncRef = useRef<IManufacturerSyncState | null>(null);

  const [
    selectedManufacturerCardsByBomId,
    setSelectedManufacturerCardsByBomId,
  ] = useState<TManufacturerCardsByBomId>({});

  const manufacturerCardsByUnitId = useMemo(() => new Map(
    availableManufacturerCards
      .map((card) => [getUnitIdFromNodeId(card.nodeId), card] as const)
      .filter((entry): entry is [number, IAICardSelection] => entry[0] !== null),
  ), [availableManufacturerCards]);

  const buildCardsFromUnits = useCallback(
    (units: IAgentSessionBomProducedByUnit[] = []) => buildManufacturerCardsFromUnits(
      manufacturerCardsByUnitId,
      units,
    ),
    [manufacturerCardsByUnitId],
  );

  const manufacturerCardType = useMemo(() => (
    activeTab?.type === VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT
      ? VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET
      : VALUE_CHAIN_UNIT_TYPES.SUPPLIER
  ), [activeTab?.type]);

  const selectedManufacturerCards = useMemo(
    () => selectedManufacturerCardsByBomId[currentBomId] || [],
    [currentBomId, selectedManufacturerCardsByBomId],
  );

  const availableActiveManufacturerCards = useMemo(
    () => availableManufacturerCards.filter((card) => card.type === manufacturerCardType),
    [availableManufacturerCards, manufacturerCardType],
  );

  const syncManufacturerCards = useCallback(async (
    bomId: number,
    cards: IAICreatorBoMUnitsTabItem[],
  ) => {
    if (!valueChainMapId || !session?.id || !bomId) {
      return;
    }

    try {
      const response = await updateAgentSessionBom({
        valueChainMapId,
        sessionId: session.id,
        bomId,
        body: {
          producedByUnits: getSortedUnitIds(cards),
        },
      }).unwrap();

      setSelectedManufacturerCardsByBomId((prev) => ({
        ...prev,
        [bomId]: response?.data?.producedByUnits
          ? buildCardsFromUnits(response.data.producedByUnits)
          : cards,
      }));
    } catch (error) {
      onBadRequestError(error);
      throw error;
    }
  }, [
    buildCardsFromUnits,
    onBadRequestError,
    session?.id,
    updateAgentSessionBom,
    valueChainMapId,
  ]);

  const flushPendingManufacturerSync = useCallback(async () => {
    const pendingSync = pendingManufacturerSyncRef.current;

    if (!pendingSync) {
      return;
    }

    pendingManufacturerSyncRef.current = null;

    try {
      await syncManufacturerCards(pendingSync.bomId, pendingSync.cards);
    } catch (error) {
      pendingManufacturerSyncRef.current = pendingSync;
      throw error;
    }
  }, [syncManufacturerCards]);

  useEffect(() => {
    if (!open || !currentBomId) {
      return;
    }

    if (pendingManufacturerSyncRef.current?.bomId === currentBomId) {
      return;
    }

    const nextManufacturerCards = buildCardsFromUnits(producedByUnits);

    setSelectedManufacturerCardsByBomId((prev) => {
      if (areUnitIdsEqual(prev[currentBomId] || [], nextManufacturerCards)) {
        return prev;
      }
      return { ...prev, [currentBomId]: nextManufacturerCards };
    });
  }, [
    buildCardsFromUnits,
    currentBomId,
    open,
    producedByUnits,
  ]);

  useEffect(() => {
    const previousBomId = previousBomIdRef.current;

    if (
      previousBomId
      && previousBomId !== currentBomId
      && pendingManufacturerSyncRef.current?.bomId === previousBomId
    ) {
      flushPendingManufacturerSync().catch(() => undefined);
    }

    previousBomIdRef.current = currentBomId;
  }, [currentBomId, flushPendingManufacturerSync]);

  useEffect(() => {
    if (!isManufacturerEditingDisabled) {
      return;
    }

    if (!currentBomId || pendingManufacturerSyncRef.current?.bomId !== currentBomId) {
      return;
    }

    pendingManufacturerSyncRef.current = null;
    setSelectedManufacturerCardsByBomId((prev) => ({
      ...prev,
      [currentBomId]: buildCardsFromUnits(producedByUnits),
    }));
  }, [
    buildCardsFromUnits,
    currentBomId,
    isManufacturerEditingDisabled,
    producedByUnits,
  ]);

  const handleManufacturerDropdownOpen = useCallback(() => {
    onAddManufacturer();
  }, [onAddManufacturer]);

  const handleManufacturerCardToggle = useCallback((card: IAICreatorBoMUnitsTabItem) => {
    if (isManufacturerEditingDisabled) {
      onBlockedManufacturerEdit();
      return;
    }

    if (!currentBomId) {
      return;
    }

    const nextManufacturerCards = selectedManufacturerCards.some((selectedCard) => selectedCard.nodeId === card.nodeId)
      ? selectedManufacturerCards.filter((selectedCard) => selectedCard.nodeId !== card.nodeId)
      : [...selectedManufacturerCards, card];

    setSelectedManufacturerCardsByBomId((prev) => ({
      ...prev,
      [currentBomId]: nextManufacturerCards,
    }));
    pendingManufacturerSyncRef.current = {
      bomId: currentBomId,
      cards: nextManufacturerCards,
    };
  }, [
    currentBomId,
    isManufacturerEditingDisabled,
    onBlockedManufacturerEdit,
    selectedManufacturerCards,
  ]);

  const handleManufacturerCardRemove = useCallback(async (cardNodeId: string) => {
    if (isManufacturerEditingDisabled) {
      onBlockedManufacturerEdit();
      return;
    }

    if (!currentBomId) {
      return;
    }

    const nextManufacturerCards = selectedManufacturerCards
      .filter((selectedCard) => selectedCard.nodeId !== cardNodeId);

    setSelectedManufacturerCardsByBomId((prev) => ({
      ...prev,
      [currentBomId]: nextManufacturerCards,
    }));
    pendingManufacturerSyncRef.current = null;

    try {
      await syncManufacturerCards(currentBomId, nextManufacturerCards);
    } catch {
      setSelectedManufacturerCardsByBomId((prev) => ({
        ...prev,
        [currentBomId]: selectedManufacturerCards,
      }));
    }
  }, [
    currentBomId,
    isManufacturerEditingDisabled,
    onBlockedManufacturerEdit,
    selectedManufacturerCards,
    syncManufacturerCards,
  ]);

  return {
    selectedManufacturerCards,
    availableActiveManufacturerCards,
    flushPendingManufacturerSync,
    handleManufacturerDropdownOpen,
    handleManufacturerCardToggle,
    handleManufacturerCardRemove,
  };
};

export default useAICreatorBoMManufacturers;
